// Stored soil values stay Mehlich-3 at 8 inches. This is a display lens applied when a number
// becomes text, never an input to arithmetic. Every factor is the ingest conversion read backwards
// a missing factor shows the stored value labelled M3.

import { displayFactor } from "./extractantFactors";
import { extractantFactors } from "../hooks/useExtractantFactors";

export type PDisplayExtractant = "m3" | "bray" | "olsen";
export type KDisplayExtractant = "m3" | "ammonium_acetate_dtpa";
export type MassBasis = "ppm" | "lbs_ac";

export interface SoilUnitPrefs {
  /* * display depth in inches; the store is always 8" */
  depthIn: number;
  pExtractant: PDisplayExtractant;
  kExtractant: KDisplayExtractant;
  /* * ppm (the stored basis) or lbs/acre — a pure display multiplier, like the extractants */
  massBasis: MassBasis;
}

export const STORED_DEPTH_IN = 8;

/*
 *
 * The depths the display lens can actually render at — compute's `depth-convert` trial range.
 *
 * Deliberately NARROWER than the `0 < d ≤ 60` accepted for a SAMPLE (BE-Light/DB, and DB declined to pin
 * this range in the database for the same reason): a sample can be TAKEN at any depth, but a lens can only
 * RENDER at a depth the v7 chain can solve backwards for. Letting a user pick 2″ would return unavailable
 * for every value and read as a data bug rather than an unsupported choice.
 */
export const MIN_DISPLAY_DEPTH_IN = 3;
export const MAX_DISPLAY_DEPTH_IN = 11;
export const DEFAULT_SOIL_UNITS: SoilUnitPrefs = {
  depthIn: STORED_DEPTH_IN,
  pExtractant: "m3",
  kExtractant: "m3",
  massBasis: "ppm",
};

type SoilUnitWire = {
  depth_in?: unknown;
  p_extractant?: unknown;
  k_extractant?: unknown;
  mass_basis?: unknown;
};

function asSoilUnitWire(raw: unknown): SoilUnitWire | null {
  if (!raw || typeof raw !== "object") return null;
  return raw as SoilUnitWire;
}

// A stored depth can sit outside the on-screen stepper. Clamping it to the default here would
// make every converted number read as unavailable.
function depthInOf(depth: unknown): number {
  return typeof depth === "number" &&
    depth >= MIN_DISPLAY_DEPTH_IN &&
    depth <= MAX_DISPLAY_DEPTH_IN
    ? depth
    : DEFAULT_SOIL_UNITS.depthIn;
}

function pExtractantOf(value: unknown): PDisplayExtractant {
  return value === "bray" || value === "olsen" || value === "m3" ? value : "m3";
}

function kExtractantOf(value: unknown): KDisplayExtractant {
  return value === "ammonium_acetate_dtpa" || value === "m3" ? value : "m3";
}

function massBasisOf(value: unknown, fallback: MassBasis): MassBasis {
  return value === "lbs_ac" || value === "ppm" ? value : fallback;
}

/*
 *
 * One set of clamps. A second copy is how display lenses drift. Callers differ only in what an
 * absent mass basis means.
 */
export function clampSoilUnitPrefs(
  raw: Partial<SoilUnitPrefs> | null | undefined,
  massFallback: MassBasis = "ppm",
): SoilUnitPrefs {
  const p = raw ?? {};
  return {
    depthIn: depthInOf(p.depthIn),
    pExtractant: pExtractantOf(p.pExtractant),
    kExtractant: kExtractantOf(p.kExtractant),
    massBasis: massBasisOf(p.massBasis, massFallback),
  };
}

function prefsFromWire(
  o: SoilUnitWire,
  massFallback: MassBasis,
): SoilUnitPrefs {
  return {
    depthIn: depthInOf(o.depth_in),
    pExtractant: pExtractantOf(o.p_extractant),
    kExtractant: kExtractantOf(o.k_extractant),
    massBasis: massBasisOf(o.mass_basis, massFallback),
  };
}

export function parseSoilUnitsWire(raw: unknown): SoilUnitPrefs | null {
  const o = asSoilUnitWire(raw);
  if (!o) return null;
  if (
    o.depth_in == null &&
    o.p_extractant == null &&
    o.k_extractant == null &&
    o.mass_basis == null
  )
    return null;
  return prefsFromWire(o, "ppm");
}

// An absent mass basis stays the value already on screen. parseSoilUnitsWire fills that gap
// with ppm, which would erase a cached choice the server has not started storing yet.
export function readUserSoilLens(
  raw: unknown,
  currentMassBasis: MassBasis,
): { prefs: SoilUnitPrefs; sawMassBasis: boolean } | null {
  const o = asSoilUnitWire(raw);
  if (!o) return null;
  return {
    sawMassBasis: "mass_basis" in o,
    prefs: prefsFromWire(o, currentMassBasis),
  };
}

/*
 *
 * The platform's furrow-slice constant is 2.4 lb/ac per ppm at 8" (the same number the Albrecht
 * engine has always used — a test pins the two against each other so they cannot drift). It scales
 * LINEARLY with depth, because the slice's soil mass does: a 6" slice holds 3/4 the soil of an 8"
 * one, so the same ppm is 3/4 the pounds.
 */
export const LBS_PER_PPM_PER_INCH = 0.3; // 2.4 at the stored 8"
export const lbsPerPpmAt = (depthIn: number): number =>
  LBS_PER_PPM_PER_INCH * depthIn;

export const P_DISPLAY_OPTIONS: { value: PDisplayExtractant; label: string }[] =
  [
    { value: "m3", label: "Mehlich-3" },
    { value: "bray", label: "Bray P1" },
    { value: "olsen", label: "Olsen" },
  ];
export const K_DISPLAY_OPTIONS: { value: KDisplayExtractant; label: string }[] =
  [
    { value: "m3", label: "Mehlich-3" },
    { value: "ammonium_acetate_dtpa", label: "Ammonium Acetate/DTPA" },
  ];
export const MASS_BASIS_OPTIONS: { value: MassBasis; label: string }[] = [
  { value: "ppm", label: "ppm" },
  { value: "lbs_ac", label: "lbs/acre" },
];

/*
 *
 * single `'k'` here, which was safe only while their factor was a shared identity — compute has
 * since given them three different numbers (1.14 / 1.15 / 1.24), and collapsing them now would run
 * Mg ~9% low on every ammonium-acetate value with nothing on screen to show it.
 */
export type Analyte = "p" | "k" | "ca" | "mg" | "other";

/*
 *
 * Identify the analyte from a layer key/title. Deliberately strict: `ph` must never be read as `p`, which
 * is the classic failure of a loose match here and would convert an acidity reading as though it were
 * phosphorus.
 */
export function analyteOf(keyOrTitle: string | null | undefined): Analyte {
  // Separators FIRST: `_` and `-` are word characters to a regex, so `\bp\b` never matches inside
  // `model__p` or `automap__grid_p_idw` — the exact keys this has to recognise. Normalising them to spaces
  // is what makes a word-boundary match mean "the token p" rather than "the letter p".
  const s = (keyOrTitle ?? "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return "other";
  // pH first: it is a token containing p, and reading an acidity value as phosphorus would convert it
  if (/\bph\b|\bp h\b|buffer ph/.test(s)) return "other";
  // Saturation percents next: `k_sat_pct` / `ca_sat_pct` normalize to "k sat pct" — the bare cation
  // token would match below and put extractant ppm→ppm factors on a dimensionless percent.
  if (/\bsat\b|saturation/.test(s)) return "other";
  if (/\bp\b|phosph|p2o5/.test(s)) return "p";
  if (/\bk\b|potass|k2o/.test(s)) return "k";
  // Ca / Mg: matched as whole whitespace TOKENS rather than by regex word boundary, so a unit like `mg/kg`
  // one token, not two — can never be read as magnesium.
  const tokens = s.split(" ").map((t) => t.replace(/[()]/g, ""));
  if (tokens.includes("ca") || tokens.includes("calcium")) return "ca";
  if (tokens.includes("mg") || tokens.includes("magnesium")) return "mg";
  return "other";
}

export interface ConvertedValue {
  value: number;
  /* * the extractant the number is actually in — NOT necessarily the one requested */
  extractant: PDisplayExtractant | KDisplayExtractant | null;
  /* * true when the requested units could not be produced and the stored value is being shown instead */
  fellBack: boolean;
}

/*
 *
 * Stored soil values stay Mehlich-3 at 8 inches. This is a display lens applied when a number
 * becomes text, never an input to arithmetic. Every factor is the ingest conversion read backwards
 * a missing factor shows the stored value labelled M3.
 */
const _warned = new Set<string>();
function warnFallbackOnce(analyte: string, token: string) {
  const k = `${analyte}|${token}`;
  if (_warned.has(k)) return;
  _warned.add(k);
  console.warn(
    `[soil units] showing ${analyte.toUpperCase()} as M3 because no ${token} factor is available — ` +
      `the served factor set is missing or failed to load (GET /api/compute/extractant-factors). ` +
      `The number is the STORED Mehlich-3 value, not a converted one.`,
  );
}

export function convertSoilValue(
  value: number,
  analyte: Analyte,
  prefs: SoilUnitPrefs,
): ConvertedValue {
  if (!Number.isFinite(value) || analyte === "other")
    return { value, extractant: null, fellBack: false };
  // P follows the phosphorus choice; K, Ca and Mg all follow the cation choice but each has its OWN factor.
  const token = analyte === "p" ? prefs.pExtractant : prefs.kExtractant;
  const f = displayFactor(extractantFactors(), analyte, token);
  // No published factor (or none fetched yet) -> show the STORED value labelled M3. Never a guess, and
  // never a remembered number from a previous factor version.
  // Say WHY once. On screen this is a bare "(M3)", which reads like the user's own choice rather than a
  // failure — "I picked Bray and it still says M3" cost a round trip to diagnose because nothing named the
  // cause. Once per session per analyte+token: enough to find it, not enough to flood.
  if (f == null) {
    warnFallbackOnce(analyte, token);
    return { value, extractant: "m3", fellBack: true };
  }
  return { value: value * f, extractant: token, fellBack: false };
}

/* * Short suffix for a converted value, or '' when the units are the system default. */
export function unitSuffix(c: ConvertedValue): string {
  if (!c.extractant) return "";
  if (c.extractant === "m3") return c.fellBack ? " (M3)" : "";
  if (c.extractant === "bray") return " (Bray)";
  if (c.extractant === "olsen") return " (Olsen)";
  return " (AA/DTPA)";
}

/* * Are these the system's own units — i.e. is every number on screen untouched? */
export function isDefaultUnits(p: SoilUnitPrefs): boolean {
  return (
    p.depthIn === STORED_DEPTH_IN &&
    p.pExtractant === "m3" &&
    p.kExtractant === "m3" &&
    p.massBasis === "ppm"
  );
}
